import React, { useEffect, useState } from 'react';
import stockService from '../../services/stockService';
import { StockMovementRequestDTO, StockMovementType } from '../../types/stock';
import alertService from '../../services/alertService';
import { formatFromRaw, getNextRawOnKey, parseRawToNumber, rawFromNumber } from '../../utils/decimalMask';
import companyService from '../../services/companyService';
import { SearchableSelect } from '../ui/SearchableSelect';
import { EmpresaResponseDTO } from '../../types/company';
import { productService } from '../../services/productService';
import { Tag } from 'lucide-react';

interface Props {
  defaultProdutoId?: string;
  defaultLocalId?: string;
  lockProduto?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const StockMovementForm: React.FC<Props> = ({ defaultProdutoId = '', defaultLocalId = '', lockProduto = false, onSuccess, onCancel }) => {
  const [tipoMovimentacao, setTipoMovimentacao] = useState<StockMovementType>('ENTRADA');
  const [produtoId, setProdutoId] = useState(defaultProdutoId);
  const QUANTIDADE_DECIMALS = 3;
  const [quantidadeRaw, setQuantidadeRaw] = useState<string>(() => rawFromNumber(0, QUANTIDADE_DECIMALS));
  const quantidade = parseRawToNumber(quantidadeRaw, QUANTIDADE_DECIMALS);
  const [numeroDocumento, setNumeroDocumento] = useState<string>('');
  const [referenciaId, setReferenciaId] = useState<number | undefined>(undefined);
  const [referenciaTabela, setReferenciaTabela] = useState<string>('');
  const [observacoes, setObservacoes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [movementTypes, setMovementTypes] = useState<string[]>([]);
  const [companies, setCompanies] = useState<EmpresaResponseDTO[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState<boolean>(false);
  const [origemId, setOrigemId] = useState<string | ''>('');
  const [destinoId, setDestinoId] = useState<string | ''>('');
  const [produtoNome, setProdutoNome] = useState<string>('');
  const [produtoCodigo, setProdutoCodigo] = useState<string>('');

  useEffect(() => {
    // Limpeza leve de campos referenciais ao trocar tipo
    setNumeroDocumento('');
    setReferenciaId(undefined);
    setReferenciaTabela('');
  }, [tipoMovimentacao]);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const resp = await stockService.getMovementTypes();
        const tipos = resp.data ?? [];
        setMovementTypes(tipos);
        // Garantir que tipo inicial exista; caso contrário, usa primeiro disponível
        if (tipos.length > 0 && !tipos.includes(tipoMovimentacao)) {
          setTipoMovimentacao(tipos[0] as StockMovementType);
        }
      } catch {
        setMovementTypes([]);
      }
    };
    loadTypes();
  }, []);

  // Carregar nome e código do produto para exibir no topo
  useEffect(() => {
    const loadProduct = async () => {
      if (!produtoId) { setProdutoNome(''); setProdutoCodigo(''); return; }
      try {
        const resp = await productService.getById(produtoId);
        if (resp.success && resp.data) {
          setProdutoNome(resp.data.nome);
          setProdutoCodigo(resp.data.codigo);
        } else {
          setProdutoNome('');
          setProdutoCodigo('');
        }
      } catch {
        setProdutoNome('');
        setProdutoCodigo('');
      }
    };
    loadProduct();
  }, [produtoId]);

  // Carregar empresas para os selects de transferência
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const page = await companyService.getCompanies({ page: 0, size: 100 });
        const opts = page?.content || [];
        setCompanies(opts);
      } catch {
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    if (tipoMovimentacao === 'TRANSFERENCIA') {
      loadCompanies();
    }
  }, [tipoMovimentacao]);

  const labelForType = (code: string) => {
    switch (code) {
      case 'ENTRADA': return 'Entrada';
      case 'SAIDA': return 'Saída';
      case 'DEVOLUCAO': return 'Devolução';
      case 'AJUSTE': return 'Ajuste';
      case 'TRANSFERENCIA': return 'Transferência';
      case 'PERDA': return 'Perda';
      case 'INVENTARIO': return 'Inventário';
      default: return code;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!produtoId || quantidade <= 0) {
      alertService.warning('Informe produto e quantidade válida');
      return;
    }

    const payload: StockMovementRequestDTO = {
      produtoId,
      tipoMovimentacao,
      quantidade,
      observacoes: observacoes || undefined,
      numeroDocumento: numeroDocumento || undefined,
      referenciaId,
      referenciaTabela: referenciaTabela || undefined,
    };

    // Para transferência, validar e adicionar origem/destino
    if (tipoMovimentacao === 'TRANSFERENCIA') {
      if (!origemId || !destinoId) {
        alertService.warning('Selecione empresa de origem e destino para transferência');
        return;
      }
      (payload as any).origemId = origemId;
      (payload as any).destinoId = destinoId;
    }

    try {
      setSubmitting(true);
      const res = await stockService.createMovement(payload);
      if (res.success) {
        alertService.success('Movimentação registrada com sucesso');
        setQuantidadeRaw(rawFromNumber(0, QUANTIDADE_DECIMALS));
        setObservacoes('');
        setNumeroDocumento('');
        setReferenciaId(undefined);
        setReferenciaTabela('');
        setOrigemId('');
        setDestinoId('');
        if (onSuccess) onSuccess();
      } else {
        alertService.error(res.message || 'Falha ao registrar movimentação');
      }
    } catch (err) {
      alertService.error('Erro ao registrar movimentação');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
      {/* Cabeçalho do produto no topo */}
      {produtoId && (
        <div className="md:col-span-2 flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <Tag className="h-5 w-5 text-red-600" />
          <div className="flex-1">
            <div className="text-sm text-gray-500">Produto</div>
            <div className="text-base font-semibold text-gray-900">{produtoNome || produtoId}</div>
            <div className="text-xs text-gray-600">Código: {produtoCodigo || '-'}</div>
          </div>
        </div>
      )}
      <div className="md:col-span-2 flex flex-col">
        <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
        <select value={tipoMovimentacao} onChange={(e) => setTipoMovimentacao(e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent">
          {movementTypes.length === 0 ? (
            <>
              <option value="ENTRADA">Entrada</option>
              <option value="SAIDA">Saída</option>
              <option value="DEVOLUCAO">Devolução</option>
              <option value="AJUSTE">Ajuste</option>
              <option value="TRANSFERENCIA">Transferência</option>
              <option value="PERDA">Perda</option>
              <option value="INVENTARIO">Inventário</option>
            </>
          ) : (
            movementTypes.map((t) => (
              <option key={t} value={t}>{labelForType(t)}</option>
            ))
          )}
        </select>
      </div>

      {/* Seletores de Empresa Origem/Destino visíveis apenas para TRANSFERENCIA */}
      {tipoMovimentacao === 'TRANSFERENCIA' && (
        <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col">
            <SearchableSelect
              options={companies.map(c => ({ id: c.id, nome: c.nomeFantasia, descricao: c.razaoSocial }))}
              value={origemId}
              onChange={(v) => setOrigemId(String(v))}
              label="Empresa Origem"
              placeholder="Selecione a empresa origem"
              loading={companiesLoading}
              onSearch={async (term) => {
                try {
                  const res = await companyService.searchCompanies(term);
                  setCompanies(res || []);
                } catch {}
              }}
            />
          </div>
          <div className="flex flex-col">
            <SearchableSelect
              options={companies.map(c => ({ id: c.id, nome: c.nomeFantasia, descricao: c.razaoSocial }))}
              value={destinoId}
              onChange={(v) => setDestinoId(String(v))}
              label="Empresa Destino"
              placeholder="Selecione a empresa destino"
              loading={companiesLoading}
              onSearch={async (term) => {
                try {
                  const res = await companyService.searchCompanies(term);
                  setCompanies(res || []);
                } catch {}
              }}
            />
          </div>
        </div>
      )}
      {!lockProduto && (
        <div className="flex flex-col">
          <label className="block text-sm font-medium text-gray-700 mb-1">Produto ID</label>
          <input
            type="text"
            value={produtoId}
            onChange={(e) => setProdutoId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="ID do produto"
          />
        </div>
      )}
      <div className="md:col-span-2 flex flex-col">
        <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade</label>
        <input
          type="text"
          value={formatFromRaw(quantidadeRaw, QUANTIDADE_DECIMALS)}
          onKeyDown={(e) => {
            const { handled, nextRaw } = getNextRawOnKey(e.key, quantidadeRaw);
            if (handled) {
              e.preventDefault();
              setQuantidadeRaw(nextRaw);
            }
          }}
          onChange={() => { /* controlado via onKeyDown */ }}
          className="w-full px-4 py-3 border-2 border-yellow-400 rounded-lg bg-yellow-50 text-2xl font-bold tracking-wide focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
          inputMode="numeric"
          placeholder={formatFromRaw(rawFromNumber(0, QUANTIDADE_DECIMALS), QUANTIDADE_DECIMALS)}
        />
      </div>
      <div className="flex flex-col">
        <label className="block text-sm font-medium text-gray-700 mb-1">Número do Documento</label>
        <input
          type="text"
          value={numeroDocumento}
          onChange={(e) => setNumeroDocumento(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          placeholder="Documento (opcional)"
        />
      </div>
      <div className="md:col-span-2 flex flex-col">
        <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
        <textarea
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          rows={3}
        />
      </div>
      {/* Footer do formulário com botões (sticky bottom para mobile) */}
      <div className="md:col-span-2 sticky bottom-0 bg-white flex items-center justify-end gap-3 px-4 py-3 border-t shadow-sm">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >Cancelar</button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >Registrar</button>
      </div>
    </form>
  );
};

export default StockMovementForm;